import React, { useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { IconButton, IconButtonProps, Stack, styled } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { DagsHistoryListItems } from './DagsHistoryListItems';
import { useDagHistory } from './hooks/useDagHistory';

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    display: 'flex',
    justifyContent: 'Center'
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  collapse: {
    paddingLeft: theme.spacing(4.5)
  },
  expandMore: {
    padding: theme.spacing(0.75)
  }
}));

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}));

type DagsHistoryTableProps = {
  identifier?: string | null;
  companyKey?: string | null;
  dagHistorySaved: boolean;
};

interface HistoryProps {
  dagrunId: string;
  ecid: string;
  dagrunStatus: string;
  dagrunStartedAt: string;
  errorMessage: string;
  message: string;
}

export const DagsHistoryTable: React.FC<DagsHistoryTableProps> = ({
  identifier,
  companyKey,
  dagHistorySaved
}: DagsHistoryTableProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const { dagHistory, error } = useDagHistory({
    companyKey: companyKey,
    connectorIdentifier: identifier,
    dagHistorySaved: dagHistorySaved
  });

  const dagrunData = dagHistory as HistoryProps[];
  const showErrorPage = !!error;

  const handleExpandClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <div data-testid="dag-history-table">
      {dagrunData.length > 0 && (
        <>
          <Stack className={classes.stack}>
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              data-testid="expandMore"
              className={classes.expandMore}
            >
              <ExpandMoreIcon />
            </ExpandMore>
            <DagsHistoryListItems dagrunData={[dagrunData[0]]} />
          </Stack>
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
            data-testid="collapse-actions"
            className={classes.collapse}
          >
            <DagsHistoryListItems dagrunData={dagrunData.slice(1)} />
          </Collapse>
        </>
      )}
    </div>
  );
};
